/**
 * Utility functions for checking IBA access based on site's country
 * This replaces user-country-based IBA access with site-country-based access
 */

import { 
  IBA_ELIGIBLE_KBA_PARTNERS, 
  IBA_ELIGIBLE_KBA_PARTNER_NAMES,
  IBA_FEATURES,
  IBA_DATA_DISPLAY,
} from 'constants/ibaAccess'
import { SITE_STATUS_CONFIRMED_ID, SITE_STATUS_NOMINATED_ID, SITE_STATUS_DELISTED_ID, SITE_STATUS_DOES_NOT_QUALIFY } from 'constants/siteStatuses'
import { BIRDLIFE_SECRETARIAT_ROLE_ID } from 'constants/roles'

import { hasIBAAccess as userHasIBAAccess } from 'utils/ibaAccess'

/**
 * Check if user has IBA access for a specific site based on site's country
 * @param {Object} profile - User profile object
 * @param {Object} site - Site object with ibaAccess property
 * @returns {boolean} - True if user has IBA access for this site
 */
export const hasSiteIBAAccess = (profile, site) => {
  if (!profile || !site?.ibaAccess) {
    return false
  }

  // BirdLife Secretariat: Use ibaAccess from backend (based on CountriesOfInterest)
  // Backend already handles the logic for BirdLife Secretariat
  if (profile.RoleId === BIRDLIFE_SECRETARIAT_ROLE_ID) {
    return site.ibaAccess.hasAccess === true
  }

  // Other users: Check both site access and user's general IBA access
  return site.ibaAccess.hasAccess && userHasIBAAccess(profile)
}

/**
 * Check if user is from IBA-eligible organizations (regardless of site country)
 * @param {Object} profile - User profile object
 * @returns {boolean} - True if user is from eligible organizations
 */
export const isFromIBAEligibleOrganizations = (profile) => {
  if (!profile || !profile.KbaPartners) {
    return false
  }

  return profile.KbaPartners.some(partner => 
    IBA_ELIGIBLE_KBA_PARTNERS.includes(partner.id)
  )
}

/**
 * Get IBA access level for a specific site
 * @param {Object} profile - User profile object
 * @param {Object} site - Site object with ibaAccess property
 * @returns {string} - 'full', 'limited', or 'none'
 */
export const getSiteIBAAccessLevel = (profile, site) => {
  if (!profile || !site?.ibaAccess) {
    return 'none'
  }

  return site.ibaAccess.accessLevel
}

/**
 * Check if user has access to specific IBA feature for a site
 * @param {Object} profile - User profile object
 * @param {Object} site - Site object with ibaAccess property
 * @param {string} feature - IBA feature type
 * @returns {boolean} - True if user has access to the feature for this site
 */
export const hasSiteIBAFeatureAccess = (profile, site, feature) => {
  if (!profile || !site?.ibaAccess) {
    return false
  }

  switch (feature) {
    case IBA_FEATURES.SITES_MANAGEMENT:
      return site.ibaAccess.canSeeSitesManagement
    case IBA_FEATURES.CONGREGATIONS:
      return site.ibaAccess.canSeeCongregations
    case IBA_FEATURES.SPECIES_FIELDS:
      return site.ibaAccess.canSeeSpeciesFields
    case IBA_FEATURES.CRITERIA:
      return site.ibaAccess.canSeeCriteria
    default:
      return false
  }
}

/**
 * Get IBA data display option for a specific site
 * @param {Object} profile - User profile object
 * @param {Object} site - Site object with ibaAccess property
 * @param {string} feature - IBA feature type
 * @returns {string} - Display option (show, dash, hide)
 */
export const getSiteIBADataDisplayOption = (profile, site, feature) => {
  if (!profile || !site || !site.ibaAccess) {
    return IBA_DATA_DISPLAY.HIDE
  }

  // BirdLife Secretariat: Use backend's ibaAccess data directly
  // Backend already determines access based on CountriesOfInterest
  if (profile.RoleId === BIRDLIFE_SECRETARIAT_ROLE_ID) {
    const canSeeFeature = hasSiteIBAFeatureAccess(profile, site, feature)
    const hasSiteAccess = site.ibaAccess.hasAccess

    if (!canSeeFeature) {
      return IBA_DATA_DISPLAY.HIDE
    }

    if (feature === IBA_FEATURES.SITES_MANAGEMENT) {
      // Sites management: Show data for IBA countries, dash for non-IBA countries
      return hasSiteAccess ? IBA_DATA_DISPLAY.SHOW_DATA : IBA_DATA_DISPLAY.SHOW_DASH
    } else {
      // Assessment features: Only show data for IBA countries
      return hasSiteAccess ? IBA_DATA_DISPLAY.SHOW_DATA : IBA_DATA_DISPLAY.HIDE
    }
  }

  // Other users: Standard logic
  const hasEligibleKbaPartner = isFromIBAEligibleOrganizations(profile)
  const hasSiteAccess = site.ibaAccess.hasAccess

  if (!hasEligibleKbaPartner) {
    return IBA_DATA_DISPLAY.HIDE
  }

  if (feature === IBA_FEATURES.SITES_MANAGEMENT) {
    // Sites management: Show data for IBA countries, dash for non-IBA countries
    return hasSiteAccess ? IBA_DATA_DISPLAY.SHOW_DATA : IBA_DATA_DISPLAY.SHOW_DASH
  } else {
    // Assessment features: Only show data for IBA countries
    return hasSiteAccess ? IBA_DATA_DISPLAY.SHOW_DATA : IBA_DATA_DISPLAY.HIDE
  }
}

/**
 * Check if user has IBA access for birds specifically for a site
 * This is used for species assessments where IBA fields are shown for birds
 * @param {Object} profile - User profile object
 * @param {Object} site - Site object with ibaAccess property
 * @param {string} speciesClassName - The class name of the species (e.g., 'AVES')
 * @returns {boolean} - True if user has IBA access for birds for this site
 */
export const hasSiteIBAAccessForBirds = (profile, site, speciesClassName = 'AVES') => {
  return speciesClassName === 'AVES' && hasSiteIBAAccess(profile, site)
}

/**
 * Get the list of IBA-eligible KBA partner names
 * @returns {Array} - Array of KBA partner names
 */
export const getIBAEligibleKbaPartnerNames = () => {
  return IBA_ELIGIBLE_KBA_PARTNER_NAMES
}

/**
 * Check if user has access to specific IBA feature for a site (legacy method)
 * @param {Object} profile - User profile object
 * @param {Object} site - Site object with ibaAccess property
 * @param {string} feature - IBA feature type
 * @returns {boolean} - True if user has access to the feature for this site
 */
export const hasIBAFeatureAccess = (profile, site, feature) => {
  return hasSiteIBAFeatureAccess(profile, site, feature)
}

/**
 * Get IBA data display option for a specific site (legacy method)
 * @param {Object} profile - User profile object
 * @param {Object} site - Site object with ibaAccess property
 * @param {string} feature - IBA feature type
 * @returns {string} - Display option (show, dash, hide)
 */
export const getIBADataDisplayOption = (profile, site, feature) => {
  return getSiteIBADataDisplayOption(profile, site, feature)
}

/**
 * Check if user has IBA access for a specific site (legacy method)
 * @param {Object} profile - User profile object
 * @param {Object} site - Site object with ibaAccess property
 * @returns {boolean} - True if user has IBA access for this site
 */
export const hasIBAAccess = (profile, site) => {
  return hasSiteIBAAccess(profile, site)
}

/**
 * Get IBA access level for a specific site (legacy method)
 * @param {Object} profile - User profile object
 * @param {Object} site - Site object with ibaAccess property
 * @returns {string} - 'full', 'limited', or 'none'
 */
export const getIBAAccessLevel = (profile, site) => {
  return getSiteIBAAccessLevel(profile, site)
}

/**
 * Check if user has IBA access for birds specifically for a site (legacy method)
 * @param {Object} profile - User profile object
 * @param {Object} site - Site object with ibaAccess property
 * @param {string} speciesClassName - The class name of the species (e.g., 'AVES')
 * @returns {boolean} - True if user has IBA access for birds for this site
 */
export const hasIBAAccessForBirds = (profile, site, speciesClassName = 'AVES') => {
  return hasSiteIBAAccessForBirds(profile, site, speciesClassName)
}

/**
 * Check if site has IBA criteria
 * @param {Object} site - Site object
 * @returns {boolean} - True if site has IBA criteria
 */
export const siteHasIBACriteria = (site) => {
  if (!site) return false
  
  // Check if site has ibaCriterias array with values
  if (site.ibaCriterias && Array.isArray(site.ibaCriterias) && site.ibaCriterias.length > 0) {
    return true
  }
  
  return false
}

/**
 * Check if BirdLife Secretariat can confirm a site
 * Site must be from IBA country, have IBA criteria, and be nominated
 * @param {Object} site - Site object
 * @param {Object} profile - User profile object
 * @returns {boolean} - True if BirdLife Secretariat can confirm
 */
export const canBirdLifeSecretariatConfirmSite = (site, profile) => {
  if (!site || !profile) return false
  
  // Check if site is from IBA country (has ibaAccess)
  if (!hasSiteIBAAccess(profile, site)) {
    return false
  }
  
  // Check if site is nominated
  if (site.SiteStatus?.id !== SITE_STATUS_NOMINATED_ID) {
    return false
  }
  
  // Check if site has IBA criteria
  if (!siteHasIBACriteria(site)) {
    return false
  }
  
  return true
}

/**
 * Check if BirdLife Secretariat can delist a site
 * Site must have iba_status_id = SITE_STATUS_CONFIRMED_ID (5) and be from IBA country
 * @param {Object} site - Site object
 * @param {Object} profile - User profile object
 * @returns {boolean} - True if BirdLife Secretariat can delist
 */
export const canBirdLifeSecretariatDelistOrSupersede = (site, profile) => {
  if (!site || !profile) return false
  
  // Check if site is from IBA country (has ibaAccess)
  if (!hasSiteIBAAccess(profile, site)) {
    return false
  }
  
  // Check if IBA status is confirmed (5)
  const ibaStatusId = site.IbaStatus?.id || site.IbaStatusHistory?.id || site.iba_status_id
  if (ibaStatusId !== SITE_STATUS_CONFIRMED_ID) {
    return false
  }
  
  // Also check that KBA status is confirmed or delisted
  if (site.SiteStatus?.id !== SITE_STATUS_CONFIRMED_ID && site.SiteStatus?.id !== SITE_STATUS_DELISTED_ID) {
    return false
  }
  
  return true
}

/**
 * Check if BirdLife Secretariat can supersede a site
 * Site must have iba_status_id = 5, 7, or 8 (BUT NOT 11) AND be from IBA country
 * AND KBA status must be confirmed or delisted (in conjunction with KBA decisions)
 * @param {Object} site - Site object
 * @param {Object} profile - User profile object
 * @returns {boolean} - True if BirdLife Secretariat can supersede
 */
export const canBirdLifeSecretariatSupersede = (site, profile) => {
  if (!site || !profile) return false
  
  // Check if site is from IBA country (has ibaAccess)
  if (!hasSiteIBAAccess(profile, site)) {
    return false
  }
  
  // IBA status must be 5, 7, or 8 (confirmed, delisted, or does not qualify) - BUT NOT 11
  const ibaStatusId = site.IbaStatus?.id || site.IbaStatusHistory?.id || site.iba_status_id
  const allowedIbaStatuses = [
    SITE_STATUS_CONFIRMED_ID,      // 5
    SITE_STATUS_DELISTED_ID,       // 7
    SITE_STATUS_DOES_NOT_QUALIFY  // 8
  ]
  
  if (!ibaStatusId || !allowedIbaStatuses.includes(ibaStatusId)) {
    return false
  }
  
  // KBA status must be confirmed or delisted to allow superseding (in conjunction with KBA decisions)
  const kbaStatusId = site.SiteStatus?.id
  const allowedKbaStatuses = [
    SITE_STATUS_CONFIRMED_ID,  // 5
    SITE_STATUS_DELISTED_ID   // 7
  ]
  
  if (!kbaStatusId || !allowedKbaStatuses.includes(kbaStatusId)) {
    return false
  }
  
  return true
}
